import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react";
import axios from "axios";
import {
  useMessagingContext,
  useUserEvent
} from "@/entities/messaging/MessagingContext";
import {
  runIdFromConflict,
  useFetchReportRun,
  useRunReportNow
} from "@/entities/savedReports/hooks";
import {
  ReportPreviewTarget,
  ReportRunDetail,
  ReportRunEventPayload
} from "@/entities/savedReports/types";
import { useAuth } from "@/services/auth/AuthContext";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";

/** The user-event type a finished report run arrives under. */
export const REPORT_RUN_EVENT_TYPE = "reportRun";

// A run nobody heard back from is read back once, then given up on. The
// server's sweep fails a stuck run at 14 minutes but sends nothing.
export const REPORT_RUN_BACKSTOP_MS = 15 * 60 * 1000;

// A read-back that got no answer is asked again after this long, rather than
// failing a run that may well have succeeded.
export const REPORT_RUN_READ_BACK_RETRY_MS = 60 * 1000;

/**
 * What reading a run back found: it is settled (applied here, or no longer
 * this tab's to settle), still generating, or the server could not be asked.
 */
type ReadBackOutcome = "settled" | "generating" | "unanswered";

/**
 * Whether a failed read-back is the server's answer rather than a lack of one.
 * A timeout or a rate limit is asked again; any other 4xx is final.
 */
const isDefiniteRefusal = (error: unknown) => {
  const status = axios.isAxiosError(error) ? error.response?.status : undefined;
  return (
    status !== undefined &&
    status >= 400 &&
    status < 500 &&
    status !== 408 &&
    status !== 429
  );
};

const isNotFound = (error: unknown) =>
  axios.isAxiosError(error) && error.response?.status === 404;

/** The generating runs, keyed so a change to anything else keeps the key. */
const generatingRunsKey = (runs: Record<string, ReportRun>) =>
  JSON.stringify(
    Object.values(runs)
      .filter((run) => run.status === "generating")
      .map((run) => [run.reportId, run.startedAt])
  );

export type ReportRunStatus = "generating" | "success" | "failed";

/** A run of a saved report this tab knows about, one per report. */
export interface ReportRun {
  reportId: string;
  /** Null only between the click and the server accepting the run. */
  runId: string | null;
  status: ReportRunStatus;
  attachmentId: string | null;
  completedAt: string | null;
  reportName: string | null;
  /** Milliseconds since the epoch, when this tab started or joined the run. */
  startedAt: number;
}

/** What the notification for a finished run is built from. */
export interface FinishedReportRun {
  reportId: string;
  runId: string;
  status: "success" | "failed";
  reportName: string | null;
}

type RunFinishedHandler = (run: FinishedReportRun) => void;

interface ReportRunsContextValue {
  runsByReportId: Record<string, ReportRun>;
  /**
   * Generates the report, or follows the run already generating for it. A run
   * the server already has in flight is joined rather than started twice.
   */
  startRun: (reportId: string, reportName: string | null) => void;
  renameReportRun: (reportId: string, name: string) => void;
  /** Drops a deleted report's run, so its result never notifies. */
  forgetReportRun: (reportId: string) => void;
  /**
   * Records whether a modal showing this report is on screen. A run that
   * finishes in front of the user needs no notification.
   */
  setReportModalOpen: (reportId: string, isOpen: boolean) => void;
  /** Registers who tells the user a run finished while its modal was closed. */
  setOnRunFinished: (handler: RunFinishedHandler | null) => void;
  /** The report the chat drawer's own preview shows, opened from elsewhere. */
  focusedReport: ReportPreviewTarget | null;
  focusReport: (report: ReportPreviewTarget) => void;
  clearFocusedReport: () => void;
}

const ReportRunsContext = createContext<ReportRunsContextValue | undefined>(
  undefined
);

const isFinished = (
  status: ReportRunDetail["status"]
): status is "success" | "failed" =>
  status === "success" || status === "failed";

// The socket hands the payload over untyped; a frame missing what the handler
// reads is dropped rather than thrown inside the socket's callback.
const isReportRunEventPayload = (
  payload: unknown
): payload is ReportRunEventPayload => {
  if (typeof payload !== "object" || payload === null) return false;
  const { reportId, runId, status } = payload as Record<string, unknown>;
  return (
    typeof reportId === "string" &&
    typeof runId === "string" &&
    (status === "success" || status === "failed")
  );
};

/**
 * The runs of saved reports the user opened, kept apart from the chat: a run
 * started from a report no longer posts a chat message. Its result arrives as
 * a `reportRun` user event, and is read back from the run itself when that
 * event may have been missed.
 */
export function ReportRunsProvider({ children }: { children: ReactNode }) {
  const { enabled: isMyReportsEnabled } = useFeatureFlag(
    FeatureFlags.CRM_10285_MY_REPORTS
  );
  const { isConnected } = useMessagingContext();
  const { tenantId } = useAuth();
  const { mutateAsync: requestRun } = useRunReportNow();
  const { mutateAsync: fetchRun } = useFetchReportRun();

  const [runsByReportId, setRunsByReportId] = useState<
    Record<string, ReportRun>
  >({});
  // Mirrors the state for reads inside callbacks that outlive a render.
  const runsRef = useRef<Record<string, ReportRun>>({});
  // Bumped on every account switch, so a read-back started in the previous
  // account cannot settle a run or announce it in this one.
  const tenantGenerationRef = useRef(0);
  const openModalCountsRef = useRef(new Map<string, number>());
  const forgottenReportIdsRef = useRef(new Set<string>());
  const onRunFinishedRef = useRef<RunFinishedHandler | null>(null);
  // Finished while nobody was registered to say so, as on the settings pages.
  const unannouncedRunsRef = useRef<FinishedReportRun[]>([]);
  const [focusedReport, setFocusedReport] =
    useState<ReportPreviewTarget | null>(null);

  const updateRuns = useCallback(
    (
      update: (runs: Record<string, ReportRun>) => Record<string, ReportRun>
    ) => {
      const next = update(runsRef.current);
      if (next === runsRef.current) return;
      runsRef.current = next;
      setRunsByReportId(next);
    },
    []
  );

  const finishRun = useCallback(
    (finished: {
      reportId: string;
      runId: string;
      status: "success" | "failed";
      attachmentId: string | null;
      completedAt: string | null;
      reportName: string | null;
    }) => {
      const known = runsRef.current[finished.reportId];
      const reportName = finished.reportName || known?.reportName || null;
      // A success with nothing to show is a failure to the user, so the notice
      // says what the preview will show.
      const status =
        finished.status === "success" && finished.attachmentId
          ? "success"
          : "failed";
      updateRuns((runs) => ({
        ...runs,
        [finished.reportId]: {
          reportId: finished.reportId,
          runId: finished.runId,
          status,
          attachmentId: status === "success" ? finished.attachmentId : null,
          completedAt: finished.completedAt,
          reportName,
          startedAt: known?.startedAt ?? Date.now()
        }
      }));
      const isShowing =
        (openModalCountsRef.current.get(finished.reportId) ?? 0) > 0;
      if (isShowing) return;
      const announced: FinishedReportRun = {
        reportId: finished.reportId,
        runId: finished.runId,
        status,
        reportName
      };
      if (onRunFinishedRef.current) onRunFinishedRef.current(announced);
      else unannouncedRunsRef.current.push(announced);
    },
    [updateRuns]
  );

  const applyRunEvent = useCallback(
    (payload: unknown) => {
      if (!isReportRunEventPayload(payload)) return;
      if (!isMyReportsEnabled) return;
      if (forgottenReportIdsRef.current.has(payload.reportId)) return;
      const known = runsRef.current[payload.reportId];
      const isTracked = known?.runId === payload.runId;
      if (isTracked && known.status !== "generating") return;
      if (!isTracked) {
        // A scheduled fire nobody joined notifies through its chat message.
        if (payload.trigger !== "manual") return;
        // This tab is following another run of the report; that run's own
        // event settles it.
        if (known?.status === "generating" && known.runId !== null) return;
        // A run that finished before this one started is an earlier run's
        // result, not this one's.
        if (
          known?.status === "generating" &&
          payload.completedAt &&
          Date.parse(payload.completedAt) < known.startedAt
        ) {
          return;
        }
      }
      finishRun({
        reportId: payload.reportId,
        runId: payload.runId,
        status: payload.status,
        attachmentId: payload.attachmentId,
        completedAt: payload.completedAt,
        reportName: payload.reportName
      });
    },
    [isMyReportsEnabled, finishRun]
  );

  useUserEvent<unknown>(REPORT_RUN_EVENT_TYPE, applyRunEvent);

  const markFailed = useCallback(
    (reportId: string, startedAt: number) => {
      const known = runsRef.current[reportId];
      if (!known || known.startedAt !== startedAt) return;
      if (known.status !== "generating") return;
      if (known.runId === null) {
        updateRuns((runs) => ({
          ...runs,
          [reportId]: { ...known, status: "failed" }
        }));
        return;
      }
      finishRun({
        reportId,
        runId: known.runId,
        status: "failed",
        attachmentId: null,
        completedAt: null,
        reportName: known.reportName
      });
    },
    [updateRuns, finishRun]
  );

  const forgetReportRun = useCallback(
    (reportId: string) => {
      forgottenReportIdsRef.current.add(reportId);
      unannouncedRunsRef.current = unannouncedRunsRef.current.filter(
        (run) => run.reportId !== reportId
      );
      updateRuns((runs) => {
        if (!runs[reportId]) return runs;
        const remaining = { ...runs };
        delete remaining[reportId];
        return remaining;
      });
      setFocusedReport((current) =>
        current?.id === reportId ? null : current
      );
    },
    [updateRuns]
  );

  // Reads a run back and applies it when it has finished. Whether a run that
  // has not finished is given up on is the caller's to decide.
  const readBack = useCallback(
    async (run: ReportRun): Promise<ReadBackOutcome> => {
      // Never accepted by the server, so there is no run to ask about.
      if (run.runId === null) return "generating";
      const tenantGeneration = tenantGenerationRef.current;
      try {
        const detail = await fetchRun({
          pathParams: { reportId: run.reportId, runId: run.runId }
        });
        if (tenantGenerationRef.current !== tenantGeneration) return "settled";
        const current = runsRef.current[run.reportId];
        if (
          current?.runId !== detail.runId ||
          current.status !== "generating"
        ) {
          return "settled";
        }
        if (!isFinished(detail.status)) return "generating";
        finishRun({
          reportId: run.reportId,
          runId: detail.runId,
          status: detail.status,
          attachmentId: detail.attachmentId,
          completedAt: detail.completedAt,
          reportName: current.reportName
        });
        return "settled";
      } catch (error) {
        if (tenantGenerationRef.current !== tenantGeneration) return "settled";
        if (!isDefiniteRefusal(error)) return "unanswered";
        const current = runsRef.current[run.reportId];
        if (current?.startedAt !== run.startedAt) return "settled";
        // The report or its run is gone, deleted on another device: there is
        // nothing left to show or announce.
        if (isNotFound(error)) forgetReportRun(run.reportId);
        else markFailed(run.reportId, run.startedAt);
        return "settled";
      }
    },
    [fetchRun, finishRun, forgetReportRun, markFailed]
  );

  const startRun = useCallback(
    (reportId: string, reportName: string | null) => {
      forgottenReportIdsRef.current.delete(reportId);
      const known = runsRef.current[reportId];
      if (known?.status === "generating") return;
      const startedAt = Date.now();
      updateRuns((runs) => ({
        ...runs,
        [reportId]: {
          reportId,
          runId: null,
          status: "generating",
          attachmentId: null,
          completedAt: null,
          reportName,
          startedAt
        }
      }));

      const followRun = (runId: string): ReportRun | null => {
        const current = runsRef.current[reportId];
        if (current?.startedAt !== startedAt) return null;
        if (current.status !== "generating") return null;
        const followed = { ...current, runId };
        updateRuns((runs) => ({ ...runs, [reportId]: followed }));
        return followed;
      };

      // The promise rather than per-call callbacks: those only fire for the
      // latest call, and two reports can be started before either answers.
      requestRun({ pathParams: { reportId } })
        .then((accepted) => followRun(accepted.runId))
        .catch((error: unknown) => {
          // Already generating on the server: follow that run instead.
          const runningId = runIdFromConflict(error);
          if (runningId) {
            // That run's event may have arrived before this answer, when the
            // run was not yet this tab's, and was dropped: ask for it once.
            const followed = followRun(runningId);
            if (followed) void readBack(followed);
            return;
          }
          markFailed(reportId, startedAt);
        });
    },
    [updateRuns, requestRun, markFailed, readBack]
  );

  // Keyed on the generating runs alone, so another report's run starting,
  // finishing or being renamed leaves this one's timers running.
  const generatingKey = generatingRunsKey(runsByReportId);
  const generatingRuns = useMemo(
    () => JSON.parse(generatingKey) as [reportId: string, startedAt: number][],
    [generatingKey]
  );

  // The backstop: a generating run still unheard from is read back, and failed
  // when it has not finished either. A read-back the server never answered says
  // nothing about the run, so it is asked again rather than failed.
  useEffect(() => {
    let isCancelled = false;
    const timers: ReturnType<typeof setTimeout>[] = [];
    const checkRun = (reportId: string, startedAt: number) => {
      // Read at the time, since the run is given its id after it starts.
      const run = runsRef.current[reportId];
      if (run?.startedAt !== startedAt || run.status !== "generating") return;
      void readBack(run).then((outcome) => {
        if (isCancelled) return;
        if (outcome === "generating") markFailed(reportId, startedAt);
        if (outcome === "unanswered") {
          timers.push(
            setTimeout(
              () => checkRun(reportId, startedAt),
              REPORT_RUN_READ_BACK_RETRY_MS
            )
          );
        }
      });
    };
    generatingRuns.forEach(([reportId, startedAt]) => {
      timers.push(
        setTimeout(
          () => checkRun(reportId, startedAt),
          Math.max(0, startedAt + REPORT_RUN_BACKSTOP_MS - Date.now())
        )
      );
    });
    return () => {
      isCancelled = true;
      timers.forEach(clearTimeout);
    };
  }, [generatingRuns, readBack, markFailed]);

  // An event sent while the socket was reconnecting is gone, so every run
  // still generating is read back once the socket is up again.
  const wasConnectedRef = useRef(isConnected);
  useEffect(() => {
    const reconnected = isConnected && !wasConnectedRef.current;
    wasConnectedRef.current = isConnected;
    if (!reconnected) return;
    Object.values(runsRef.current)
      .filter((run) => run.status === "generating")
      .forEach((run) => {
        void readBack(run);
      });
  }, [isConnected, readBack]);

  // Runs belong to the account they were started in. Switching accounts keeps
  // this provider mounted, so what it knows is dropped here instead, before
  // paint, so a read-back resolving in between finds the switch already made.
  const tenantIdRef = useRef(tenantId);
  useLayoutEffect(() => {
    if (tenantIdRef.current === tenantId) return;
    tenantIdRef.current = tenantId;
    tenantGenerationRef.current += 1;
    runsRef.current = {};
    setRunsByReportId({});
    forgottenReportIdsRef.current.clear();
    unannouncedRunsRef.current = [];
    setFocusedReport(null);
  }, [tenantId]);

  const renameReportRun = useCallback(
    (reportId: string, name: string) => {
      updateRuns((runs) =>
        runs[reportId]
          ? { ...runs, [reportId]: { ...runs[reportId], reportName: name } }
          : runs
      );
      setFocusedReport((current) =>
        current?.id === reportId ? { ...current, name } : current
      );
    },
    [updateRuns]
  );

  const setReportModalOpen = useCallback(
    (reportId: string, isOpen: boolean) => {
      const counts = openModalCountsRef.current;
      const count = (counts.get(reportId) ?? 0) + (isOpen ? 1 : -1);
      if (count > 0) counts.set(reportId, count);
      else counts.delete(reportId);
    },
    []
  );

  const setOnRunFinished = useCallback(
    (handler: RunFinishedHandler | null) => {
      onRunFinishedRef.current = handler;
      if (!handler) return;
      const unannounced = unannouncedRunsRef.current;
      unannouncedRunsRef.current = [];
      unannounced.forEach((run) => handler(run));
    },
    []
  );

  const focusReport = useCallback(
    (report: ReportPreviewTarget) => setFocusedReport(report),
    []
  );
  const clearFocusedReport = useCallback(() => setFocusedReport(null), []);

  const value = useMemo(
    () => ({
      runsByReportId,
      startRun,
      renameReportRun,
      forgetReportRun,
      setReportModalOpen,
      setOnRunFinished,
      focusedReport,
      focusReport,
      clearFocusedReport
    }),
    [
      runsByReportId,
      startRun,
      renameReportRun,
      forgetReportRun,
      setReportModalOpen,
      setOnRunFinished,
      focusedReport,
      focusReport,
      clearFocusedReport
    ]
  );

  return (
    <ReportRunsContext.Provider value={value}>
      {children}
    </ReportRunsContext.Provider>
  );
}

export function useReportRuns() {
  const context = useContext(ReportRunsContext);
  if (context === undefined) {
    throw new Error("useReportRuns must be used within a ReportRunsProvider");
  }
  return context;
}

/** The run this tab knows about for a report, if any. */
export const useReportRun = (reportId: string | null | undefined) => {
  const { runsByReportId } = useReportRuns();
  return reportId ? runsByReportId[reportId] ?? null : null;
};
